import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { DateTime } from "luxon";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { useSnackbar } from "notistack";

import { useWidget } from "/imports/state/WidgetContext";
import { useAuth } from "/imports/state/AuthContext";
import { useBackTo } from "/imports/ui/components/discovery";
import { useUrlFilters, dateUtils } from "/imports/libs/url";
import api from "/imports/api/client";
import { track } from "/imports/libs/analytics";
import useClientPreferences from "/imports/hooks/useClientPreferences";
import usePackComposer from "/imports/hooks/usePackComposer";
import {
  TIME_BUCKETS,
  bucketIdOf,
  buildWeekDates,
  bookingWindowMaxDate,
  groupSlotsByTime,
  ALL_DURATIONS,
  getServiceDurationOptions,
  filterGroupsByPreferredTime,
  pickDefaultSport,
  resolvePreferredSportService,
  resolvePreferredLocationId,
  getRecommendedSlot
} from "/imports/ui/components/booking";
import { getOptionsLabel } from "./courtHelpers";
import useCourtReschedule from "./useCourtReschedule";

export default function useCourtBooking() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { slug, services, sports, locations, selectedLocation, addToCart, currency, saas } = useWidget();
  const goBack = useBackTo(`/${slug}`);
  const { isAuthenticated } = useAuth();

  // "Today" is the club's calendar day, not the device's or UTC's: a client
  // behind the club's clock just after its midnight must not land on — or be
  // offered — a day the club has already closed.
  const clubZone = saas?.timeZone || "Asia/Dubai";
  const todayISO = dateUtils.clubToday(clubZone);
  const tomorrowISO = DateTime.fromISO(todayISO).plus({ days: 1 }).toISODate();
  const thisWeekStart = DateTime.fromISO(todayISO).startOf("week").toISODate();

  const { filters, setFilter, setFilters } = useUrlFilters({
    date: todayISO,
    duration: ""
  });

  // Pack-composition mode (?packProduct=…): slot taps toggle into a pack
  // selection instead of booking, and a bottom tray drives the purchase.
  const pack = usePackComposer();

  // Reschedule mode (?reschedule=<bookingId>): the times a paid court booking can move
  // to, with the service and duration pinned to the booking's own. Bumping reloadKey refetches.
  const [reloadKey, setReloadKey] = useState(0);
  const reschedule = useCourtReschedule({ slug, bookingId: filters.reschedule || null, onStale: () => setReloadKey((k) => k + 1) });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [availability, setAvailability] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [expandedTime, setExpandedTime] = useState(null);
  // Mobile-only UI state: which filter/option bottom sheet is open.
  const [activeSheet, setActiveSheet] = useState(null); // 'sport'|'service'|'court'|'duration'|'location'|'options'|null

  // Desktop view state.
  const [viewMode, setViewMode] = useState("day"); // desktop only: 'day' | 'week'
  const [weekStart, setWeekStart] = useState(() => thisWeekStart); // ISO Monday
  const [weekSlotsByDate, setWeekSlotsByDate] = useState(null); // Map<dateISO, slots[]>
  const [optionsAnchor, setOptionsAnchor] = useState(null); // multi-option popover anchor el
  const [optionsGroup, setOptionsGroup] = useState(null); // group whose options are shown
  const [optionsDate, setOptionsDate] = useState(null); // date the popover slots belong to

  const courtServices = services?.filter((s) => s.categoryType === "category-service-court") || [];

  const availableSports = useMemo(() => {
    const sportIds = new Set(courtServices.map((s) => s.sportId).filter(Boolean));
    return (sports || []).filter((s) => sportIds.has(s._id));
  }, [courtServices, sports]);

  const sportFilteredServices = useMemo(() => {
    if (!filters.sport) return courtServices;
    return courtServices.filter((s) => s.sportId === filters.sport);
  }, [courtServices, filters.sport]);

  const availableFacilities = useMemo(() => {
    return (availability?.facilities || []).filter((f) => !filters.locationId || f.locationId === filters.locationId);
  }, [availability?.facilities, filters.locationId]);

  // Whether the club lets clients pick a specific court for this service. When
  // off, the chooser is replaced by an inventory panel and a time chip books the
  // court the picker chose outright. Absent flag = allowed (legacy services).
  const allowCourtPick = sportFilteredServices.find((s) => s._id === filters.serviceId)?.widgetAllowResourcePick !== false;

  // Drop a specific court carried in the URL (shared link, service switch) when
  // the selected service doesn't allow picking one.
  useEffect(() => {
    if (!allowCourtPick && filters.facilityId) setFilter("facilityId", null);
  }, [allowCourtPick, filters.facilityId, setFilter]);

  // Duration options come straight from the selected service's pricing
  // (client-side), so the picker is populated without an availability fetch.
  // Duration is required before anything is fetched; an effect below auto-selects
  // a default (60 min, else the longest option) so the client lands on results.
  const durationOptions = useMemo(
    () =>
      getServiceDurationOptions(
        (sportFilteredServices || []).find((s) => s._id === filters.serviceId),
        filters.locationId
      ),
    [sportFilteredServices, filters.serviceId, filters.locationId]
  );

  // Only the locations the selected service is actually offered at — picking any
  // other org location just yields "No availability". Fall back to all locations
  // for legacy services that don't pin a location set.
  // A court being moved goes only where it costs what was paid — the server says where.
  const rescheduleLocationIds = reschedule.info?.locationIds;
  const serviceLocations = useMemo(() => {
    if (rescheduleLocationIds) return (locations || []).filter((l) => rescheduleLocationIds.includes(l._id));
    const svc = (sportFilteredServices || []).find((s) => s._id === filters.serviceId);
    const ids = svc?.locationIds || [];
    if (!ids.length) return locations || [];
    return (locations || []).filter((l) => ids.includes(l._id));
  }, [sportFilteredServices, filters.serviceId, locations, rescheduleLocationIds]);

  const weekDates = useMemo(() => buildWeekDates(weekStart), [weekStart]);

  // Last selectable date under the service's booking window (null = unlimited).
  // The server drops out-of-window slots regardless; this bounds the pickers.
  // Pack mode additionally caps at the pack's validity window.
  const serviceMaxDate = useMemo(
    () =>
      bookingWindowMaxDate(
        (sportFilteredServices || []).find((s) => s._id === filters.serviceId),
        DateTime.now().setZone(clubZone)
      ),
    [sportFilteredServices, filters.serviceId, clubZone]
  );
  const maxBookableDate =
    pack.active && pack.packMaxDate ? (serviceMaxDate && serviceMaxDate < pack.packMaxDate ? serviceMaxDate : pack.packMaxDate) : serviceMaxDate;

  // Pack mode pins the filters the variant dictates: the linked service, its
  // sport, the sold tier's duration, and (when the purchase fixed one) the
  // location. Batched so the service/sport coherence effects never fight it.
  useEffect(() => {
    if (!pack.resolved) return;
    const next = {};
    const linked = courtServices.find((s) => s._id === pack.opt.linkedServiceId);
    if (linked?.sportId && filters.sport !== linked.sportId) next.sport = linked.sportId;
    if (filters.serviceId !== pack.opt.linkedServiceId) next.serviceId = pack.opt.linkedServiceId;
    if (pack.opt.sessionDuration && filters.duration !== String(pack.opt.sessionDuration)) next.duration = String(pack.opt.sessionDuration);
    if (pack.packLocationId && filters.locationId !== pack.packLocationId) next.locationId = pack.packLocationId;
    if (Object.keys(next).length > 0) setFilters(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pack.resolved, pack.opt, filters.sport, filters.serviceId, filters.duration, filters.locationId]);

  // Reschedule mode pins the booking's own service, its sport and its duration — the move
  // never changes them. Until the server has answered, the URL from the dashboard stands in.
  const rescheduleFrom = reschedule.info?.from;
  const pinnedServiceId = rescheduleFrom?.serviceId || (reschedule.active ? filters.serviceId : null);
  useEffect(() => {
    if (!pinnedServiceId) return;
    const next = {};
    const service = courtServices.find((s) => s._id === pinnedServiceId);
    if (service?.sportId && filters.sport !== service.sportId) next.sport = service.sportId;
    if (filters.serviceId !== pinnedServiceId) next.serviceId = pinnedServiceId;
    if (rescheduleFrom && filters.duration !== String(rescheduleFrom.duration)) next.duration = String(rescheduleFrom.duration);
    if (Object.keys(next).length > 0) setFilters(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pinnedServiceId, rescheduleFrom, courtServices.length, filters.sport, filters.serviceId, filters.duration]);

  // Force a concrete sport selection so the Sport filter is never empty and the
  // chip layout stays predictable (every filter always has a value/position).
  // Reschedule mode takes the booking's own sport instead (above).
  useEffect(() => {
    if (reschedule.active) return;
    if (availableSports.length > 0 && !filters.sport) {
      setFilter("sport", pickDefaultSport(availableSports)._id);
    }
  }, [availableSports, filters.sport, setFilter, reschedule.active]);

  // Keep the selected service valid for the chosen sport: auto-pick the first
  // matching service on load and re-pick if the current one no longer matches.
  // A resolved pack owns the service (pinned above) — even one that isn't in the
  // online list, which this effect would otherwise swap out for ever, refetching
  // availability on every flip.
  useEffect(() => {
    if (reschedule.active || pack.resolved || sportFilteredServices.length === 0) return;
    const valid = sportFilteredServices.some((s) => s._id === filters.serviceId);
    if (!filters.serviceId || !valid) {
      setFilter("serviceId", sportFilteredServices[0]._id);
    }
  }, [sportFilteredServices, filters.serviceId, setFilter, reschedule.active, pack.resolved]);

  // Clear a stale duration the selected service doesn't offer (e.g. carried in
  // the URL after switching services) so the picker falls back to its empty
  // "choose a duration" state rather than silently querying an invalid value.
  useEffect(() => {
    if (!filters.duration || !durationOptions.length) return;
    if (filters.duration === ALL_DURATIONS) {
      // "All" of one length is just that length.
      if (durationOptions.length === 1) setFilter("duration", String(durationOptions[0].value));
      return;
    }
    if (!durationOptions.some((o) => String(o.value) === filters.duration)) {
      setFilter("duration", "");
    }
  }, [durationOptions, filters.duration, setFilter]);

  // Auto-select a default duration once a service is chosen. Several lengths:
  // "All durations" — the client picks the time first and the length in the
  // picker, so a start hidden for one length still shows for the others (gap
  // minimization). One length: that one. Pack mode pins the variant's length,
  // so it keeps a concrete default (60, else the longest). Only fills an empty
  // value, so an explicit choice (or a URL param) is preserved.
  useEffect(() => {
    if (reschedule.active || filters.duration || !durationOptions.length) return;
    if (durationOptions.length > 1 && !pack.active) {
      setFilter("duration", ALL_DURATIONS);
      return;
    }
    const has60 = durationOptions.some((o) => o.value === 60);
    const pick = has60 ? 60 : Math.max(...durationOptions.map((o) => o.value));
    setFilter("duration", String(pick));
  }, [durationOptions, filters.duration, pack.active, setFilter, reschedule.active]);

  // Move a date (or week) the club has already left behind forward to today —
  // a stale URL, or a page left open across the club's midnight.
  useEffect(() => {
    if (filters.date && filters.date < todayISO) setFilter("date", todayISO);
    if (weekStart < thisWeekStart) setWeekStart(thisWeekStart);
  }, [filters.date, todayISO, weekStart, thisWeekStart, setFilter]);

  // Drop a date (or week) that fell outside the booking window — e.g. carried
  // in the URL or left over after switching to a shorter-window service.
  useEffect(() => {
    if (!maxBookableDate) return;
    if (filters.date > maxBookableDate) {
      setFilter("date", todayISO > maxBookableDate ? maxBookableDate : todayISO);
    }
    if (weekStart > maxBookableDate) setWeekStart(thisWeekStart);
  }, [maxBookableDate, filters.date, weekStart, todayISO, thisWeekStart, setFilter]);

  // Keep the location selection coherent with the service. When the service is
  // offered at a single location, lock the filter to it so its name stays
  // visible (rather than a confusing "All Locations" that hides where it is).
  // Otherwise just drop a stale location the service doesn't serve.
  useEffect(() => {
    if (serviceLocations.length === 1) {
      if (filters.locationId !== serviceLocations[0]._id) setFilter("locationId", serviceLocations[0]._id);
      return;
    }
    if (filters.locationId && !serviceLocations.some((l) => l._id === filters.locationId)) {
      setFilter("locationId", null);
    }
  }, [serviceLocations, filters.locationId, setFilter]);

  useEffect(() => {
    // Duration is required: with no service or no duration chosen there is
    // nothing to fetch — the view shows the "choose a duration" prompt instead.
    if (!slug || !filters.serviceId || !filters.duration) {
      setAvailability(null);
      setWeekSlotsByDate(null);
      setLoading(false);
      return undefined;
    }
    let cancelled = false;

    (async () => {
      setLoading(true);
      setError(null);
      try {
        const baseParams = {
          serviceId: filters.serviceId,
          facilityId: filters.facilityId,
          // "All durations": no length on the request — the server probes every
          // length the service sells and the groups carry one slot per length.
          duration: filters.duration && filters.duration !== ALL_DURATIONS ? filters.duration : undefined,
          locationId: filters.locationId || selectedLocation?._id
        };

        const fetchDay = (date) =>
          reschedule.active
            ? api.getCourtRescheduleSlots(slug, reschedule.bookingId, { date, locationId: filters.locationId, facilityId: filters.facilityId })
            : api.getCourtAvailability(slug, { date, ...baseParams });

        // Searches that find nothing are demand the club can't serve — the
        // slot count is the point of this event. A move is not a search for
        // something new, so it stays out of the figures.
        const trackSearch = (days, slotsFound) => {
          if (reschedule.active) return;
          track("availability_searched", {
            kind: "court",
            service_id: baseParams.serviceId,
            service_name: courtServices.find((s) => s._id === baseParams.serviceId)?.name || null,
            location_id: baseParams.locationId || null,
            facility_id: baseParams.facilityId || null,
            duration: baseParams.duration || "all",
            date: filters.date,
            days_searched: days,
            slots_found: slotsFound
          });
        };

        if (isMobile || viewMode === "day") {
          // Mobile, and desktop Day mode, both show a single selected day.
          const data = await fetchDay(filters.date);
          if (!cancelled) {
            setAvailability(data);
            setWeekSlotsByDate(null);
            if (data?.reschedule) reschedule.setInfo(data.reschedule);
            trackSearch(1, data?.slots?.length || 0);
          }
          return;
        }

        // Desktop week grid: fetch the 7 visible days in parallel. Past days
        // can't be booked, so we skip the network call and treat them as empty.
        const datesToFetch = weekDates.filter((d) => d >= todayISO);
        // A booking being moved asks for the whole week at once, so the server checks it once.
        const fetchRescheduleWeek = async () => {
          if (!datesToFetch.length) return [];
          const week = await api.getCourtRescheduleWeek(slug, reschedule.bookingId, {
            from: datesToFetch[0],
            days: datesToFetch.length,
            locationId: filters.locationId,
            facilityId: filters.facilityId
          });
          return datesToFetch.map((d, i) => (i === 0 ? week : { slots: week.days?.[d] || [] }));
        };
        const results = reschedule.active ? await fetchRescheduleWeek() : await Promise.all(datesToFetch.map(fetchDay));
        if (cancelled) return;
        const moveInfo = results.find((r) => r?.reschedule)?.reschedule;
        if (moveInfo) reschedule.setInfo(moveInfo);
        trackSearch(
          datesToFetch.length,
          results.reduce((n, r) => n + (r?.slots?.length || 0), 0)
        );
        const byDate = new Map(weekDates.map((d) => [d, []]));
        datesToFetch.forEach((d, i) => byDate.set(d, results[i]?.slots || []));
        setWeekSlotsByDate(byDate);
        // Courts/durations are date-independent — take them from the first day
        // that returned a payload to drive the sidebar filters.
        setAvailability(results.find(Boolean) || null);
      } catch (err) {
        if (cancelled || (reschedule.active && reschedule.leaveIfChanged(err))) return;
        setError(err.message || "Failed to load availability");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    slug,
    filters.date,
    filters.serviceId,
    filters.facilityId,
    filters.duration,
    filters.locationId,
    selectedLocation?._id,
    isMobile,
    viewMode,
    weekDates,
    todayISO,
    reloadKey,
    reschedule.active
  ]);

  // Selected-day groups (mobile).
  const groupedSlots = useMemo(() => groupSlotsByTime(availability?.slots), [availability?.slots]);

  // The active client's saved preferences — drives both the "Suggested for you"
  // section and the "Use my preferences" filter shortcut. Empty for guests.
  const preferences = useClientPreferences(slug);

  // "Suggested for you" — the loaded slots that fall inside the client's
  // preferred time windows for the selected day. Derived client-side from data
  // already in hand (no extra slot fetch); empty when nothing matches.
  const suggestedGroups = useMemo(
    () => (reschedule.active ? [] : filterGroupsByPreferredTime(groupedSlots, preferences.timeRanges, filters.date)),
    [groupedSlots, preferences.timeRanges, filters.date, reschedule.active]
  );

  // The Sport + Service the "Use my preferences" button would select, limited to
  // what's offered here. The button shows only when a preferred sport or service
  // actually applies, so it never points at something unbookable.
  const preferredSelection = useMemo(
    () =>
      resolvePreferredSportService({
        sports: preferences.sports,
        services: preferences.services,
        availableSports,
        categoryServices: courtServices,
        currentSport: filters.sport,
        currentService: filters.serviceId
      }),
    [preferences.sports, preferences.services, availableSports, courtServices, filters.sport, filters.serviceId]
  );
  // The preferred location to apply, restricted to what the currently selected
  // service is offered at — drives whether the button shows on location alone.
  const preferredLocationId = useMemo(
    () => resolvePreferredLocationId(preferences.locations, serviceLocations),
    [preferences.locations, serviceLocations]
  );
  const canApplyPreferences = !reschedule.active && preferences.isAuthenticated && (preferredSelection.hasPreference || !!preferredLocationId);

  // Snap Sport + Service (+ preferred Location) to the client's preferences in
  // one click. Court resets to "Any". Duration: auto-apply when the preferred
  // service offers exactly one option; with several, leave it for the client.
  // Location is resolved against the service being applied (not the current one)
  // and only set when bookable there, so the coherence effect never clears it.
  const applyPreferences = () => {
    const svc = courtServices.find((s) => s._id === preferredSelection.serviceId) || null;
    const durs = getServiceDurationOptions(svc);
    const duration = durs.length === 1 ? String(durs[0].value) : filters.duration || "";
    const targetLocations = svc?.locationIds?.length ? (locations || []).filter((l) => svc.locationIds.includes(l._id)) : locations || [];
    const locationId = resolvePreferredLocationId(preferences.locations, targetLocations);
    const next = { sport: preferredSelection.sportId, serviceId: preferredSelection.serviceId, facilityId: null, duration };
    if (locationId) next.locationId = locationId;
    setFilters(next);
  };

  // Per-day groups for the desktop week grid.
  const weekGrouped = useMemo(() => {
    const m = new Map();
    if (weekSlotsByDate) {
      for (const [d, slots] of weekSlotsByDate) m.set(d, groupSlotsByTime(slots));
    }
    return m;
  }, [weekSlotsByDate]);

  // Split the time groups into non-empty time-of-day buckets for the mobile
  // segmented control.
  const bucketedSlots = useMemo(() => {
    const byId = new Map();
    for (const group of groupedSlots) {
      const id = bucketIdOf(group.time);
      if (!byId.has(id)) byId.set(id, []);
      byId.get(id).push(group);
    }
    return TIME_BUCKETS.filter((b) => byId.has(b.id)).map((b) => ({ ...b, groups: byId.get(b.id) }));
  }, [groupedSlots]);

  const expandedGroup = groupedSlots.find((g) => g.time === expandedTime);

  // Reset the inline picker / sheets / popover when the underlying query changes.
  useEffect(() => {
    setExpandedTime(null);
    setActiveSheet(null);
    setOptionsAnchor(null);
    setOptionsGroup(null);
    setOptionsDate(null);
  }, [filters.date, filters.serviceId, filters.facilityId, filters.duration, filters.locationId, weekStart]);

  const handleSlotSelect = (slot, date = filters.date) => {
    // Pack mode: taps compose the pack instead of booking one court slot.
    if (pack.active) {
      pack.togglePick(slot, date);
      closeOptionsPopover();
      closeSheet();
      return;
    }
    // Reschedule mode: a picked time is confirmed in place of being booked.
    if (reschedule.active) {
      reschedule.select(slot, date);
      closeOptionsPopover();
      closeSheet();
      return;
    }
    setSelectedSlot(slot);
    addToCart("court", {
      type: "court",
      date,
      time: slot.time,
      duration: slot.duration,
      facilityId: slot.facilityId,
      facilityName: slot.facilityName,
      // "Any court" resolved by getRecommendedSlot; an explicit court pick has no flag.
      facilityAuto: !!slot.facilityAuto,
      serviceId: slot.serviceId,
      pricingId: slot.pricingId,
      price: slot.price,
      currency: slot.currency,
      locationId: slot.locationId,
      locationName: slot.locationName
    });
    if (isAuthenticated) {
      navigate(`/${slug}/checkout`);
    } else {
      enqueueSnackbar("Please sign in to complete your booking", { variant: "warning" });
      navigate(`/${slug}/login?redirect=checkout`);
    }
  };

  const handleSportChange = (sportId) => {
    const matching = sportId ? courtServices.filter((s) => s.sportId === sportId) : courtServices;
    setFilters({ sport: sportId, serviceId: matching[0]?._id || filters.serviceId, facilityId: null, duration: "" });
  };

  // Court assigned by the club: the picker would offer a lone "Any Court" row,
  // so book the recommended slot outright. Several locations still need the
  // picker — the branch remains the client's choice.
  const autoBookedSlot = (group) => {
    if (allowCourtPick) return null;
    // Several lengths at this time: the length is still the client's call.
    if ((group.durations || []).length > 1) return null;
    const locations = new Set((group.slots || []).map((s) => s.locationId).filter(Boolean));
    return locations.size > 1 ? null : getRecommendedSlot(group);
  };

  const handleGroupClick = (group) => {
    // Pack mode: a time that's already picked unpicks on tap, whichever
    // court row it came through.
    if (pack.active && pack.isPicked(filters.date, group.time)) {
      pack.togglePick(group.slots[0], filters.date);
      return;
    }
    if (group.slots.length === 1) {
      // Only one court free: the client is offered no choice at all, so the
      // court is the system's pick — flagged like "Any court", not an explicit tap.
      handleSlotSelect(getRecommendedSlot(group));
      return;
    }
    const auto = autoBookedSlot(group);
    if (auto) {
      handleSlotSelect(auto);
      return;
    }
    if (isMobile) {
      // On mobile the multi-option picker is a bottom sheet rather than an
      // inline expansion that pushes the rest of the page down.
      setExpandedTime(group.time);
      setActiveSheet("options");
    } else {
      setExpandedTime((cur) => (cur === group.time ? null : group.time));
    }
  };

  // Desktop week-grid / day-card chip click: single slot books immediately;
  // multi opens a popover anchored to the chip so the user can pick court/location.
  const handleWeekChipClick = (e, group, date) => {
    if (pack.active && pack.isPicked(date, group.time)) {
      pack.togglePick(group.slots[0], date);
      return;
    }
    if (group.slots.length === 1) {
      // See handleGroupClick: a lone court is the system's pick, not a choice.
      handleSlotSelect(getRecommendedSlot(group), date);
      return;
    }
    const auto = autoBookedSlot(group);
    if (auto) {
      handleSlotSelect(auto, date);
      return;
    }
    setOptionsGroup(group);
    setOptionsDate(date);
    setOptionsAnchor(e.currentTarget);
  };

  const closeOptionsPopover = () => {
    setOptionsAnchor(null);
    setOptionsGroup(null);
    setOptionsDate(null);
  };

  const goPrevWeek = () => setWeekStart(DateTime.fromISO(weekStart).minus({ days: 7 }).toISODate());
  // A week starting past the booking window has nothing bookable — stop there.
  const canGoNextWeek = !maxBookableDate || DateTime.fromISO(weekStart).plus({ days: 7 }).toISODate() <= maxBookableDate;
  const goNextWeek = () => {
    if (canGoNextWeek) setWeekStart(DateTime.fromISO(weekStart).plus({ days: 7 }).toISODate());
  };

  const closeSheet = () => {
    setActiveSheet(null);
    setExpandedTime(null);
  };

  // ---- Mobile filter chip + bottom-sheet definitions ----------------------
  const selectedService = sportFilteredServices.find((s) => s._id === filters.serviceId);
  const selectedCourt = availableFacilities.find((f) => f._id === filters.facilityId);
  const selectedLocationObj = locations?.find((l) => l._id === filters.locationId);
  const selectedSport = availableSports.find((s) => s._id === filters.sport);

  const sheetTitles = {
    sport: "Select Sport",
    service: "Select Service",
    court: "Select Court",
    duration: "Select Duration",
    location: "Select Location",
    options: expandedGroup ? `${expandedGroup.time} · Choose your option` : "Choose your option"
  };

  return {
    navigate,
    goBack,
    slug,
    currency,
    locations,
    isMobile,
    isAuthenticated,
    filters,
    setFilter,
    setFilters,
    loading,
    error,
    availability,
    setSelectedSlot,
    viewMode,
    setViewMode,
    todayISO,
    tomorrowISO,
    thisWeekStart,
    weekStart,
    weekDates,
    weekGrouped,
    maxBookableDate,
    canGoNextWeek,
    courtServices,
    availableSports,
    sportFilteredServices,
    availableFacilities,
    allowCourtPick,
    durationOptions,
    serviceLocations,
    groupedSlots,
    suggestedGroups,
    bucketedSlots,
    expandedGroup,
    activeSheet,
    setActiveSheet,
    optionsAnchor,
    optionsGroup,
    optionsDate,
    canApplyPreferences,
    applyPreferences,
    pack,
    reschedule,
    handleSlotSelect,
    handleSportChange,
    handleGroupClick,
    handleWeekChipClick,
    closeOptionsPopover,
    goPrevWeek,
    goNextWeek,
    closeSheet,
    getOptionsLabel: (slots) => getOptionsLabel(slots, serviceLocations.length > 1, filters.duration === ALL_DURATIONS),
    selectedService,
    selectedCourt,
    selectedLocationObj,
    selectedSport,
    sheetTitles
  };
}
